import { Box, Drawer, List, ListItem, ListItemButton, Stack, Typography } from '@mui/material';
import { KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../assets/corporate-logos/gaia/white_icon.png';
import fullLogoWhite from '../../assets/corporate-logos/gaia/gaia-logo-all-white.png';
import { FunctionComponent, ReactElement, useContext } from 'react';
import { IRoleType } from '../../shared/services/user-details/user-details.model';
import { UserDetailsContext } from '../../context/user-details/user-details';
import { ScreenLabelsContext } from '../../context/screen-labels/screen-labels';
import { TooltipHover } from '../../components/presentation-components/miscellaneous-components/tooltip-hover';
import { INavButton, navArray, partnerArray, superAdminArray } from '../../shared/data/navigation.data';
import { IconPicker } from '../../components/presentation-components/display-elements-components/icon-picker';
import { userHasRequiredRole as hasRequiredRole } from '../../shared/utils/role-utils';

// Collapsed = the original 56px icon rail; expanded adds a text label beside each icon.
// 240px matches the inner nav's structural width and fits the longest label
// ("Product Carbon Footprint") without truncating.
export const LEFT_NAV_COLLAPSED_WIDTH = 56;
export const LEFT_NAV_EXPANDED_WIDTH = 240;

const navItemButtonSx = (isActive: boolean, expanded: boolean) => ({
  borderRadius: '10px',
  p: 0,
  m: 1,
  justifyContent: expanded ? 'flex-start' : 'center',
  '&:hover': {
    backgroundColor: 'primary.light'
  },
  backgroundColor: isActive ? 'primary.light' : 'primary'
});

export const LeftNavbar: FunctionComponent<{
  expanded: boolean,
  onToggleExpanded: () => void,
  /** Hidden on compact desktop widths, where the rail is forced collapsed. */
  showToggle?: boolean,
}> = ({ expanded, onToggleExpanded, showToggle = true }) => {

  const location = useLocation();
  const userDetailsContext = useContext(UserDetailsContext)
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const userDetails = userDetailsContext.userDetails

  const drawerWidth = expanded ? LEFT_NAV_EXPANDED_WIDTH : LEFT_NAV_COLLAPSED_WIDTH;

  function userHasRequiredRole(allowedRoles: IRoleType[]): boolean {
    return hasRequiredRole(userDetails, allowedRoles)
  }

  const navItemContent = (object: INavButton, isActive: boolean, displayName: string) => (
    <>
      <Box sx={{ p: 1, minWidth: 4 }}>
        <IconPicker icon={isActive ? object.activeIcon : object.icon} color={'common.white'} />
      </Box>
      {expanded &&
        <Typography variant='body2' noWrap sx={{ color: 'common.white', pr: 1 }}>
          {displayName}
        </Typography>}
    </>
  )

  // The label is visible when expanded, so the hover tooltip only exists on the
  // collapsed rail.
  const withCollapsedTooltip = (button: ReactElement, text: string) =>
    expanded ? button : (
      <TooltipHover text={text} placement={'right'} backgroundColor={'common.white'}>
        {button}
      </TooltipHover>
    );

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          backgroundColor: 'primary.main',
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
      variant="permanent"
      anchor="left"
    >
      {/* Fixed, non-shrinking slot so swapping between the mark and the full wordmark on
          expand/collapse never reflows the list below it. flexShrink 0 matters: the drawer
          paper is a flex column and the Stack below overflows it, so without it the slot
          gets squeezed to its content's min height, which differs per mode and made every
          icon shift vertically on toggle. */}
      {/* Top-aligned at the site's original geometry: 24px above the logo, items starting
          at 60px in BOTH modes (so nothing shifts on toggle). The 36px wordmark fills the
          24-60px band exactly; the 20px mark leaves the same 16px gap the site always had. */}
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingBottom: 0, paddingTop: 24, height: '60px', boxSizing: 'border-box', flexShrink: 0 }}>
        <Link to={userDetails?.organisation.name === null ? '/settings/company-settings/overview' : userDetails?.isPartner && !userDetails?.isLoggedInAsClient ? '/partner-dashboard' : '/dashboard'}>
          {(() => {
            const partnerBranded = ((userDetails?.isPartner && userDetails?.isLoggedInAsClient) || (!userDetails?.isPartner && userDetails?.partnerOrganisation))
            const partnerIcon = partnerBranded ? userDetails?.partnerOrganisation?.partnerIconFilepath : null
            const partnerReversedLogo = partnerBranded ? userDetails?.partnerOrganisation?.partnerReversedLogoFilepath : null
            // A partner client must never see the Gaia wordmark, so without a reversed
            // partner logo their mark stays in both states.
            const markSrc = partnerBranded ? partnerIcon : logo
            const wordmarkSrc = partnerBranded ? partnerReversedLogo : fullLogoWhite
            const showWordmark = expanded && !!wordmarkSrc
            // Both images stay mounted from first render, so each is fetched and decoded
            // at app load; expand/collapse only flips CSS. Mounting the wordmark lazily
            // made the first toggle flicker while its bytes arrived.
            return (
              <>
                {wordmarkSrc &&
                  <img src={wordmarkSrc} alt='logo' style={{ height: '36px', maxWidth: '190px', objectFit: 'contain', display: showWordmark ? 'block' : 'none' }} />}
                {markSrc
                  ? <img src={markSrc} alt='logo' style={{ width: '20px', height: '20px', objectFit: partnerBranded ? 'cover' : 'contain', display: showWordmark ? 'none' : 'block' }} />
                  : !showWordmark && <span style={{ display: 'inline-block', width: '20px', height: '20px' }} />}
              </>
            )
          })()}
        </Link>
      </Box>
      {/* flex 1 (not height 100%) so the list fills the space under the fixed logo slot
          instead of overflowing the flex-column drawer paper. */}
      <Stack sx={{ flex: 1, minHeight: 0 }}>
        {!(userDetails?.isPartner && !userDetails?.isLoggedInAsClient) && <List sx={{ flex: 1 }}>
          {navArray.map((object: INavButton, index: number) => {
            if (object.name === 'Product Carbon Footprint' && !userDetails?.organisation.enableProductCarbonFootprint) {
              return null;
            }
            const isActive = object.isActive(location.pathname);
            const path = object.name === 'Organisation' && userDetails?.organisation.enableSubsidiaries ? '/organisation/subsidiaries' : object.to
            const displayName = object.name === 'Organisation' ? screenLabelsContext.getLabel('organisation', { plural: false, capitalize: 'first' }) : object.name;
            return (
              userHasRequiredRole(object.allowedRoles) &&
              <ListItem
                key={index}
                disablePadding>
                {withCollapsedTooltip(
                  <ListItemButton component={Link} to={path} disableRipple sx={navItemButtonSx(isActive, expanded)}
                    disabled={!userDetails?.organisation.subscriptionActive || userDetails.organisation.name === null}
                  >
                    {navItemContent(object, isActive, displayName)}
                  </ListItemButton>,
                  displayName
                )}
              </ListItem>
            );
          })}
        </List>}
        {userDetails?.isPartner && !userDetails?.isLoggedInAsClient &&
          <List sx={{ py: 0 }}>
            {partnerArray.map((object: INavButton, index: number) => {
              const isActive = object.isActive(location.pathname);
              return (
                <ListItem
                  key={index}
                  disablePadding>
                  {withCollapsedTooltip(
                    <ListItemButton component={Link} to={object.to} disableRipple sx={navItemButtonSx(isActive, expanded)}>
                      {navItemContent(object, isActive, object.name)}
                    </ListItemButton>,
                    object.name
                  )}
                </ListItem>
              );
            })}
          </List>
        }
        {userDetails?.isSuperAdmin &&
          <List sx={{ py: 0 }}>
            {superAdminArray.map((object: INavButton, index: number) => {
              const isActive = object.isActive(location.pathname);
              return (
                <ListItem
                  key={index}
                  disablePadding>
                  {withCollapsedTooltip(
                    <ListItemButton component={Link} to={object.to} disableRipple sx={navItemButtonSx(isActive, expanded)}>
                      {navItemContent(object, isActive, object.name)}
                    </ListItemButton>,
                    object.name
                  )}
                </ListItem>
              );
            })}
          </List>
        }
        {showToggle && <List sx={{ mt: 'auto', pt: 0 }}>
          <ListItem disablePadding>
            {withCollapsedTooltip(
              <ListItemButton
                onClick={onToggleExpanded}
                disableRipple
                aria-label={expanded ? 'Collapse navigation' : 'Expand navigation'}
                // mt 0: with the list paddings gone, the icon above's own margin provides the
                // 8px gap, matching the spacing between the top-bar icons.
                sx={{ ...navItemButtonSx(false, expanded), mt: 0 }}
              >
                <Box sx={{ p: 1, minWidth: 4, display: 'flex' }}>
                  {expanded ?
                    <KeyboardDoubleArrowLeft sx={{ color: 'common.white' }} /> :
                    <KeyboardDoubleArrowRight sx={{ color: 'common.white' }} />}
                </Box>
                {expanded &&
                  <Typography variant='body2' noWrap sx={{ color: 'common.white', pr: 1 }}>
                    Collapse
                  </Typography>}
              </ListItemButton>,
              'Expand navigation'
            )}
          </ListItem>
        </List>}
      </Stack>
    </Drawer>
  );
}
